// Marketplace — Product Detail (Marketplace/Partner initiative, Phase 1, Inc 3). The employer's
// full view of a partner product before connecting the partner and enabling it: what it is, who
// gains, who provides & regulates it, eligibility, pricing (partner-set), the exact data shared
// under consent, and the request → approve-partner → enable → live workflow. Reuses the shared
// category model + the app design system. Pricing/interest are partner-set (never asserted here);
// the partner underwrites & decides — KaziPeople shares consented signals that help the decision.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Building2, ShieldCheck, CheckCircle2, Clock, Lock, Coins, HeartPulse,
  FileText, Users, Landmark, ArrowRight, Info,
} from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { creditApi, type MarketplaceProduct, type PartnerRelationship } from '@/features/credit/api/creditApi';
import { formatMoney } from '../payroll/lib/format';
import { categoryFor, productBadge, isLending, groupKeyFor } from './categories';
import PartnerEnablementDrawer from './PartnerEnablementDrawer';

// Agreement status → the employer↔partner connection state that gates everything below it.
type Conn = 'not_connected' | 'requested' | 'awaiting_approval' | 'connected';
function connectionOf(status: string | null | undefined): Conn {
  switch (status) {
    case 'pending': return 'requested';
    case 'employer_review': return 'awaiting_approval';
    case 'active': return 'connected';
    default: return 'not_connected'; // null | declined | revoked
  }
}
const CONN_BADGE: Record<Conn, { label: string; variant: 'warning' | 'info' | 'success' | 'secondary' }> = {
  not_connected: { label: 'Not connected', variant: 'warning' },
  requested: { label: 'Pending review', variant: 'info' },
  awaiting_approval: { label: 'Awaiting your approval', variant: 'warning' },
  connected: { label: 'Connected', variant: 'success' },
};

// Category-aware default copy — a meaningful page for any product until partners author richer
// content in the Studio. Never asserts pricing/interest (partner-set).
function overview(p: MarketplaceProduct): string {
  const partner = p.partnerName || 'the partner';
  if (isLending(p)) {
    return `${p.name} is a financing product offered through KaziPeople by ${partner}. It is provided and funded by the partner, who makes the credit decision. KaziPeople handles discovery, employer approval, consent and the payroll deduction — it does not lend, hold funds, or make that decision. With the employee's consent it shares signals that help the partner decide (a KaziScore, a verified income band and employment status) and powers automatic repayment through payroll.`;
  }
  const k = groupKeyFor(p);
  if (k === 'insurance' || k === 'benefits') {
    return `${p.name} is a protection product offered through KaziPeople by ${partner}. The partner underwrites and administers the cover; KaziPeople handles discovery, employer approval, consent and any payroll contribution. KaziPeople does not underwrite, hold premiums, or make eligibility decisions.`;
  }
  return `${p.name} is a partner service offered through KaziPeople by ${partner}. The partner provides and is responsible for the service; KaziPeople handles discovery, employer approval, consent and any payroll integration — it does not hold funds or make partner decisions.`;
}
function employerBenefits(p: MarketplaceProduct): string[] {
  if (isLending(p)) return [
    'A financial safety net for your team that you do not fund.',
    'Advances and repayments flow through existing payroll — zero reconciliation.',
    'You control eligible groups, caps, effective dates and approval rules.',
    'No balance-sheet risk — the partner carries the credit.',
  ];
  if (['insurance', 'benefits'].includes(groupKeyFor(p))) return [
    'Meaningful protection for employees without running an insurance scheme.',
    'Enrolment and any contribution handled inside payroll.',
    'You choose which groups are eligible and any employer contribution.',
    'The partner carries the risk and the regulated obligations.',
  ];
  return [
    'A vetted partner service your employees can access in one place.',
    'Managed alongside payroll with clear controls.',
    'You choose who is eligible and when it goes live.',
    'The partner remains responsible for delivery and compliance.',
  ];
}
function employeeBenefits(p: MarketplaceProduct): string[] {
  if (isLending(p)) return [
    'Access to earned pay in an emergency.',
    'Clear, upfront pricing — shown before they confirm.',
    'Automatic repayment through payroll — nothing to remember.',
    'Private — colleagues never see a request.',
  ];
  if (['insurance', 'benefits'].includes(groupKeyFor(p))) return [
    'Protection they might not access on their own.',
    'Any contribution handled through payroll.',
    'Clear cover and terms shown before they enrol.',
    'Private and voluntary.',
  ];
  return [
    'A trusted service offered through their employer.',
    'Clear terms shown before they opt in.',
    'Handled securely alongside payroll.',
    'Private and voluntary.',
  ];
}

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<MarketplaceProduct | null>(null);
  const [rel, setRel] = useState<PartnerRelationship | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [acting, setActing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await creditApi.marketplaceProducts();
      const found = list.find((p) => p.id === id) ?? null;
      setProduct(found);
      setNotFound(!found);
      // The partner-level relationship gates everything below the product — fetch it alongside.
      if (found) {
        try { setRel(await creditApi.partnerRelationship(found.partnerId)); }
        catch { setRel(null); }
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not load this product.'));
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const conn = useMemo(() => connectionOf(product?.agreementStatus), [product]);
  const partnerApproved = rel?.status === 'approved';

  const connect = async () => {
    if (!product) return;
    setActing(true);
    try {
      await creditApi.requestAccess(product.id);
      toast.success('Request sent — the partner will review it. Nothing is shared until you approve.');
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not send the request.'));
    } finally {
      setActing(false);
    }
  };
  const approve = async () => {
    if (!product) return;
    setActing(true);
    try {
      // The agreement id equals the product id on this surface (employer_review agreement).
      await creditApi.approveTenantAgreement(product.id);
      toast.success('Partner approved. You can now enable the product for your employees.');
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not approve the partner.'));
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
        <Skeleton className="h-8 w-40" /><Skeleton className="h-24 rounded-xl" />
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]"><Skeleton className="h-96 rounded-xl" /><Skeleton className="h-72 rounded-xl" /></div>
      </div>
    );
  }
  if (notFound || !product) {
    return (
      <div className="mx-auto max-w-2xl p-10 text-center">
        <p className="text-muted-foreground">This product isn’t available, or you don’t have access to it.</p>
        <Button variant="outline" className="mt-4 gap-1" onClick={() => navigate('../..', { relative: 'path' })}>
          <ArrowLeft className="h-4 w-4" /> Back to Marketplace
        </Button>
      </div>
    );
  }

  const cat = categoryFor(product);
  const bdg = productBadge(product);
  const cb = CONN_BADGE[conn];
  const CatIcon = cat.icon;

  return (
    <div className="mx-auto max-w-6xl p-4 md:p-6">
      <button className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        onClick={() => navigate('../..', { relative: 'path' })}>
        <ArrowLeft className="h-4 w-4" /> Back to {cat.label}
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start gap-4">
        <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 text-lg font-bold text-white">
          {(product.partnerName || 'P').slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{product.partnerName || 'Partner'}</span>
            <Badge variant="success" className="gap-1"><ShieldCheck className="h-3 w-3" /> Verified partner</Badge>
            <span>· {cat.label}</span>
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-charcoal">{product.name}</h1>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant={bdg.variant}>{bdg.label}</Badge>
            <Badge variant="secondary">For employees</Badge>
            <Badge variant="success" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Available</Badge>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        {/* LEFT */}
        <div className="space-y-4">
          <Card><CardContent className="space-y-6 p-5 md:p-6">
            <Section icon={<Info className="h-4 w-4" />} title="Overview">
              <p className="text-sm text-muted-foreground">{overview(product)}</p>
            </Section>
            <Section icon={<Landmark className="h-4 w-4" />} title="What the employer gains">
              <BenefitGrid items={employerBenefits(product)} />
            </Section>
            <Section icon={<Users className="h-4 w-4" />} title="What employees gain">
              <BenefitGrid items={employeeBenefits(product)} />
            </Section>
            <Section icon={<CheckCircle2 className="h-4 w-4" />} title="Pricing & fees">
              <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2 text-sm">
                {isLending(product) && product.maxAmount > 0 && (
                  <><dt className="text-muted-foreground">Amount</dt>
                    <dd className="text-right font-medium text-foreground tabular-nums">
                      {formatMoney(product.minAmount, product.currency)}–{formatMoney(product.maxAmount, product.currency)}</dd></>
                )}
                {!isLending(product) && product.maxAmount > 0 && (
                  <><dt className="text-muted-foreground">Coverage</dt>
                    <dd className="text-right font-medium text-foreground tabular-nums">up to {formatMoney(product.maxAmount, product.currency)}</dd></>
                )}
                <dt className="text-muted-foreground">Interest &amp; fees</dt><dd className="text-right font-medium text-foreground">Set by the partner</dd>
                <dt className="text-muted-foreground">Employer cost</dt><dd className="text-right font-medium text-foreground">None to enable</dd>
              </dl>
              <p className="mt-2.5 text-xs text-muted-foreground">Pricing, interest and fees are defined in the partner’s product and agreement — the exact figures are shown to the employee before they confirm. KaziPeople charges the employer nothing for enabling it.</p>
            </Section>
          </CardContent></Card>

          {/* Data-sharing & consent */}
          <Card className="border-info/40 bg-info-soft">
            <CardContent className="p-5 md:p-6">
              <Section icon={<Lock className="h-4 w-4" />} title="Data-sharing & consent" accent>
                <p className="text-sm text-info-strong">Nothing is shared and nobody is enrolled until an authorized approver at your organization consents. When you enable this product, the following is shared with the partner:</p>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-info-strong" /><span><b className="text-foreground">Employer:</b> company name, TIN, participating employee group size.</span></li>
                  <li className="flex gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-info-strong" /><span><b className="text-foreground">Per applying employee (only when they apply):</b> name, employee number, <b className="text-foreground">KaziScore</b>, verified net-pay band and employment status, salary-account reference — the signals that support the partner’s decision.</span></li>
                  <li className="flex gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-info-strong" /><span><b className="text-foreground">Purpose:</b> eligibility &amp; the partner’s decision · <b className="text-foreground">Retention:</b> for the life of the agreement · <b className="text-foreground">Withdraw:</b> disable any time.</span></li>
                </ul>
                <p className="mt-3 text-xs text-muted-foreground">No national IDs, no full salary history, and no bank balances are shared. The consent is recorded against a signed agreement version and appears in your audit trail.</p>
              </Section>
            </CardContent>
          </Card>

          {/* How enabling works */}
          <Card><CardContent className="p-5 md:p-6">
            <Section icon={<ArrowRight className="h-4 w-4" />} title="How enabling it works">
              <ol className="space-y-0">
                <Step n={1} title="Approve the partner">Select &amp; approve the partner (and its data-sharing terms). This unlocks their products for your organization.</Step>
                <Step n={2} title="Enable the product">Request this product; an authorized approver confirms benefits, disclosures and consent.</Step>
                <Step n={3} title="Partner review">The partner reviews the agreement; may request more information.</Step>
                <Step n={4} title="Configure">Choose eligible groups, caps, effective date and deduction authorization.</Step>
                <Step n={5} title="Live for employees" last>Eligible employees now see it in My Products and can apply; you can pause any time.</Step>
              </ol>
              <p className="mt-3 rounded-lg bg-muted px-3 py-2.5 text-xs text-muted-foreground">
                Employees only ever see products from partners <b className="text-foreground">you have approved</b> and products <b className="text-foreground">you have enabled</b> — never before.
              </p>
            </Section>
          </CardContent></Card>
        </div>

        {/* RIGHT — sticky action panel */}
        <aside className="space-y-4 lg:sticky lg:top-4">
          <Card><CardContent className="p-5">
            <Row label="Status"><Badge variant="success" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Available</Badge></Row>
            <Row label="Partner">
              {partnerApproved
                ? <Badge variant="success" className="gap-1"><ShieldCheck className="h-3 w-3" /> Approved</Badge>
                : <Badge variant="warning">Not approved</Badge>}
            </Row>
            {partnerApproved && <Row label="Product"><Badge variant={cb.variant}>{cb.label}</Badge></Row>}

            <div className="mt-4 space-y-2">
              {/* GATE: the partner must be approved (org-level + consent) before any product action. */}
              {!partnerApproved && (
                <Button className="w-full gap-1.5" onClick={() => setDrawerOpen(true)}>
                  Review &amp; enable product <ArrowRight className="h-4 w-4" />
                </Button>
              )}
              {partnerApproved && conn === 'not_connected' && (
                <Button className="w-full gap-1.5" disabled={acting} onClick={connect}>
                  {acting ? 'Requesting…' : <>Request this product <ArrowRight className="h-4 w-4" /></>}
                </Button>
              )}
              {partnerApproved && conn === 'requested' && <Button className="w-full" variant="outline" disabled>Requested — pending partner review</Button>}
              {partnerApproved && conn === 'awaiting_approval' && (
                <Button className="w-full gap-1.5" disabled={acting} onClick={approve}>
                  {acting ? 'Approving…' : <>Review &amp; approve agreement <CheckCircle2 className="h-4 w-4" /></>}
                </Button>
              )}
              {partnerApproved && conn === 'connected' && (
                <Button className="w-full gap-1.5" variant="outline" onClick={() => navigate('../..', { relative: 'path' })}>
                  Manage in Marketplace <ArrowRight className="h-4 w-4" />
                </Button>
              )}
              <Button variant="ghost" className="w-full">Contact partner</Button>
            </div>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
              <Lock className="h-3 w-3" /> {partnerApproved
                ? 'Partner approved. Requests are reviewed before anything goes live.'
                : 'Approve the partner first, then enable. Nothing is shared until you approve.'}
            </p>

            <div className="mt-4 space-y-3 border-t border-border pt-4">
              <Fact icon={<ShieldCheck className="h-4 w-4" />} title="Provided & regulated by">{product.partnerName || 'The partner'} — a regulated partner, licensed by its financial regulator.</Fact>
              <Fact icon={<Clock className="h-4 w-4" />} title="Setup time">~2 business days after approval.</Fact>
              <Fact icon={<Coins className="h-4 w-4" />} title="Employer cost">None to enable.</Fact>
              <Fact icon={<CheckCircle2 className="h-4 w-4" />} title="Approval">Authorized approver + consent required.</Fact>
            </div>
          </CardContent></Card>

          <Card className="bg-muted/40"><CardContent className="p-4">
            <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-charcoal">
              <ShieldCheck className="h-3.5 w-3.5" /> Who’s responsible
            </h3>
            <p className="text-xs text-muted-foreground">
              The partner underwrites, funds and services the product and owns the regulated decision. KaziPeople governs listing, consent and audit, and — with consent — supplies the signals (KaziScore, verified income &amp; employment) that inform that decision. Your organization does not hold or advance funds.
            </p>
          </CardContent></Card>
        </aside>
      </div>

      <PartnerEnablementDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        product={product}
        onCompleted={load}
      />
    </div>
  );
}

// --- small presentational helpers ---
const Section: React.FC<{ icon: React.ReactNode; title: string; accent?: boolean; children: React.ReactNode }> = ({ icon, title, accent, children }) => (
  <section>
    <div className="mb-3 flex items-center gap-2.5">
      <span className={`grid h-7 w-7 place-items-center rounded-lg ${accent ? 'bg-info/15 text-info-strong' : 'bg-accent text-primary-500'}`}>{icon}</span>
      <h2 className={`text-base font-semibold ${accent ? 'text-info-strong' : 'text-charcoal'}`}>{title}</h2>
    </div>
    {children}
  </section>
);
const BenefitGrid: React.FC<{ items: string[] }> = ({ items }) => (
  <div className="grid gap-2.5 sm:grid-cols-2">
    {items.map((t, i) => (
      <div key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
        <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-success-soft text-success-strong">✓</span>
        <span>{t}</span>
      </div>
    ))}
  </div>
);
const Step: React.FC<{ n: number; title: string; last?: boolean; children: React.ReactNode }> = ({ n, title, last, children }) => (
  <li className="flex gap-3">
    <div className="flex flex-col items-center">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] border-primary-500 bg-card text-xs font-bold text-primary-500">{n}</span>
      {!last && <span className="mt-1 w-px flex-1 bg-border" />}
    </div>
    <div className="pb-4">
      <b className="block text-sm text-foreground">{title}</b>
      <span className="text-[13px] text-muted-foreground">{children}</span>
    </div>
  </li>
);
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
    {children}
  </div>
);
const Fact: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="flex gap-2.5 text-[13px]">
    <span className="mt-0.5 shrink-0 text-primary-500">{icon}</span>
    <span><b className="block text-foreground">{title}</b><span className="text-muted-foreground">{children}</span></span>
  </div>
);
